<script lang="ts">
	import type { IssueCategory, Report } from '#lib/types';
	import { i18n, t } from '#lib/i18n/state.svelte';
	import { issueTextEn } from '#lib/i18n/issues';
	import { SEVERITY_UI, severityLabel, categoryShort, confidenceLabel } from '#lib/ui/labels';
	import { excerpt } from '#lib/text/normalize';

	interface Props {
		report: Report;
	}
	let { report }: Props = $props();

	const groups = $derived.by(() => {
		const map = new Map<IssueCategory, Report['issues']>();
		for (const issue of report.issues) {
			const list = map.get(issue.category) ?? [];
			list.push(issue);
			map.set(issue.category, list);
		}
		return [...map.entries()].map(([key, items]) => ({ key, items }));
	});

	function text(issue: Report['issues'][number]) {
		const en = i18n.lang === 'en' ? issueTextEn(issue) : null;
		return { title: en?.title ?? issue.title, detail: en?.detail ?? issue.detail };
	}

	let showUndecided = $state(true);
</script>

<div class="appendix">
	<div class="row-between head">
		<div>
			<h3>{t('method.appendixTitle')}</h3>
			<p class="small muted">{t('method.appendixBody')}</p>
		</div>
		<label class="checkbox">
			<input type="checkbox" bind:checked={showUndecided} />
			{t('method.appendixUndecided')}
		</label>
	</div>

	{#each groups as g (g.key)}
		{@const items = g.items.filter((i) => showUndecided || i.decisive)}
		{#if items.length}
			<section class="group">
				<h4>{categoryShort(g.key)} <span class="mono count">{items.length}</span></h4>
				<ol class="log">
					{#each items as issue (issue.id)}
						{@const txt = text(issue)}
						{@const pageLabel =
							issue.location.page !== undefined
								? t('common.page', { n: issue.location.page })
								: issue.location.sourceLine !== undefined
									? t('common.line', { n: issue.location.sourceLine })
									: ''}
						<li class="entry" class:undecided={!issue.decisive}>
							<div class="entry-head">
								<span class="chip {SEVERITY_UI[issue.severity].chip}">{severityLabel(issue.severity)}</span>
								<strong>{txt.title}</strong>
								<span class="loc mono">{pageLabel}</span>
								<span class="conf">{issue.confidence === 'low' ? confidenceLabel('low') : ''}</span>
							</div>
							<p class="detail">{txt.detail}</p>
							{#if issue.quote}
								<blockquote>{excerpt(issue.quote, 260)}</blockquote>
							{/if}
							<span class="code mono">{issue.code}</span>
						</li>
					{/each}
				</ol>
			</section>
		{/if}
	{/each}

	{#if report.issues.length === 0}
		<p class="muted small">{t('findings.none')}</p>
	{/if}
</div>

<style>
	.appendix {
		margin-top: 1rem;
	}
	.head {
		align-items: flex-start;
		margin-bottom: 0.8rem;
	}
	.head h3 {
		font-family: var(--font-ui);
		font-size: 0.92rem;
		font-weight: 650;
		margin: 0;
	}
	.head p {
		margin: 0.15rem 0 0;
	}
	.checkbox {
		display: inline-flex;
		align-items: center;
		gap: 0.4rem;
		font-size: 0.78rem;
		color: var(--muted);
		cursor: pointer;
		white-space: nowrap;
	}
	.group {
		margin-bottom: 1rem;
	}
	.group h4 {
		font-family: var(--font-ui);
		font-size: 0.78rem;
		font-weight: 650;
		text-transform: uppercase;
		letter-spacing: 0.05em;
		color: var(--muted);
		margin: 0 0 0.4rem;
	}
	.count {
		font-weight: 400;
	}
	.log {
		list-style: none;
		margin: 0;
		padding: 0;
		display: grid;
		gap: 0.4rem;
	}
	.entry {
		border: 1px solid var(--line);
		border-radius: 9px;
		background: #fff;
		padding: 0.65rem 0.8rem;
		display: grid;
		gap: 0.25rem;
	}
	.entry.undecided {
		opacity: 0.72;
		background: var(--paper);
	}
	.entry-head {
		display: flex;
		align-items: center;
		gap: 0.45rem;
		flex-wrap: wrap;
		font-size: 0.85rem;
	}
	.loc {
		margin-left: auto;
		color: var(--muted);
		font-size: 0.7rem;
	}
	.conf {
		font-size: 0.7rem;
		color: var(--muted);
	}
	.detail {
		margin: 0;
		font-size: 0.8rem;
		color: var(--ink-2);
	}
	blockquote {
		margin: 0.15rem 0 0;
		padding: 0.35rem 0.6rem;
		border-left: 2px solid var(--line-strong);
		background: var(--paper);
		font-size: 0.76rem;
		font-style: italic;
		color: var(--muted);
	}
	.code {
		font-size: 0.66rem;
		color: var(--muted);
	}
</style>
